import React, { useEffect, useState } from "react";
import { Loader2, Plus, Trash2, Save, ChevronUp, ChevronDown, RotateCcw, MessageCircle, Languages } from "lucide-react";
import { fetchAdminQuickRequests, saveAdminQuickRequests, resetAdminQuickRequests } from "../../lib/api";
import { QUICK_ICONS, quickIcon } from "../../lib/quickRequests";
import { toast } from "../../hooks/use-toast";

const inputCls = "w-full rounded-lg border border-[#e8dcc8] bg-white px-3 py-2 text-sm focus:border-[#4a1c17] focus:ring-2 focus:ring-[#4a1c17]/20 outline-none";
const LANGS = ["en", "es", "fr", "de"];

function I18nFields({ value, onChange, testId }) {
  const [open, setOpen] = useState(false);
  const v = value || {};
  return (
    <div className="mt-1">
      <button type="button" onClick={() => setOpen((o) => !o)} className="text-[11px] text-[#7c5a4e] hover:text-[#4a1c17] inline-flex items-center gap-1" data-testid={`${testId}-toggle`}>
        <Languages className="h-3 w-3" /> {open ? "Nascondi traduzioni" : `Traduzioni: ${LANGS.map((l) => (v[l] ? l.toUpperCase() : `${l.toUpperCase()}✗`)).join(" ")}`}
      </button>
      {open && (
        <div className="grid grid-cols-2 gap-1.5 mt-1.5">
          {LANGS.map((l) => (
            <label key={l} className="text-[10px] uppercase text-[#a08477] flex items-center gap-1.5">
              <span className="w-5 shrink-0 font-bold">{l}</span>
              <input value={v[l] || ""} onChange={(e) => onChange({ ...v, [l]: e.target.value })} className={`${inputCls} !py-1 !text-xs normal-case`} data-testid={`${testId}-${l}`} />
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function QuickItemRow({ item, idx, total, onChange, onRemove, onMove }) {
  const Icon = quickIcon(item.icon);
  return (
    <div className="rounded-xl border border-[#e8dcc8] bg-[#faf3ee] p-3" data-testid={`qr-item-${idx}`}>
      <div className="flex items-start gap-2">
        <div className="h-9 w-9 shrink-0 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 flex items-center justify-center"><Icon className="h-4 w-4" /></div>
        <div className="flex-1 grid gap-2 sm:grid-cols-[1fr_180px]">
          <input value={item.label} onChange={(e) => onChange({ label: e.target.value })} placeholder="Etichetta (es. Asciugamani)" className={inputCls} data-testid={`qr-item-label-${idx}`} />
          <select value={item.icon} onChange={(e) => onChange({ icon: e.target.value })} className={inputCls} data-testid={`qr-item-icon-${idx}`}>
            {Object.entries(QUICK_ICONS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <textarea value={item.text} onChange={(e) => onChange({ text: e.target.value })} rows={2} placeholder="Testo del messaggio WhatsApp (resta in italiano)" className={`${inputCls} sm:col-span-2`} data-testid={`qr-item-text-${idx}`} />
          <div className="sm:col-span-2"><I18nFields value={item.label_i18n} onChange={(v) => onChange({ label_i18n: v })} testId={`qr-item-i18n-${idx}`} /></div>
        </div>
        <div className="flex flex-col gap-1 shrink-0">
          <button type="button" onClick={() => onMove(-1)} disabled={idx === 0} className="p-1 text-[#7c5a4e] hover:text-[#4a1c17] disabled:opacity-30" data-testid={`qr-item-up-${idx}`}><ChevronUp className="h-4 w-4" /></button>
          <button type="button" onClick={() => onMove(1)} disabled={idx === total - 1} className="p-1 text-[#7c5a4e] hover:text-[#4a1c17] disabled:opacity-30" data-testid={`qr-item-down-${idx}`}><ChevronDown className="h-4 w-4" /></button>
          <button type="button" onClick={onRemove} className="p-1 text-red-600 hover:text-red-800" data-testid={`qr-item-remove-${idx}`}><Trash2 className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}

export default function QuickRequestsPanel() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { setCfg(await fetchAdminQuickRequests()); }
    catch (e) { toast({ title: "Errore caricamento", description: e?.response?.data?.detail || e.message }); }
  };
  useEffect(() => { load(); }, []);

  const set = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const setItem = (i, patch) => setCfg((c) => ({ ...c, items: c.items.map((it, k) => (k === i ? { ...it, ...patch } : it)) }));
  const removeItem = (i) => setCfg((c) => ({ ...c, items: c.items.filter((_, k) => k !== i) }));
  const moveItem = (i, d) => setCfg((c) => {
    const items = [...c.items]; const j = i + d;
    if (j < 0 || j >= items.length) return c;
    [items[i], items[j]] = [items[j], items[i]];
    return { ...c, items };
  });
  const addItem = () => setCfg((c) => ({ ...c, items: [...c.items, { id: "", label: "", icon: "info", text: "" }] }));

  const save = async () => {
    setSaving(true);
    try {
      const { intro, ask_title, ask_subtitle, ask_text, items, ask_title_i18n, ask_subtitle_i18n } = cfg;
      const data = await saveAdminQuickRequests({ intro, ask_title, ask_subtitle, ask_text, items, ask_title_i18n, ask_subtitle_i18n });
      setCfg((c) => ({ ...c, ...data }));
      toast({ title: "Richieste rapide salvate", description: "Traduzioni EN/ES/FR/DE aggiornate automaticamente per i testi modificati." });
    } catch (e) { toast({ title: "Errore salvataggio", description: e?.response?.data?.detail || e.message }); }
    finally { setSaving(false); }
  };
  const reset = async () => {
    if (!window.confirm("Ripristinare i testi e le scorciatoie predefinite?")) return;
    try { const data = await resetAdminQuickRequests(); setCfg((c) => ({ ...c, ...data })); toast({ title: "Default ripristinati" }); }
    catch (e) { toast({ title: "Errore", description: e?.response?.data?.detail || e.message }); }
  };

  if (!cfg) return <div className="py-6 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-[#4a1c17]" /></div>;

  return (
    <section className="rounded-xl bg-white border border-[#e8dcc8] p-4 shadow-sm" data-testid="quick-requests-panel">
      <div className="flex flex-wrap items-center gap-2 mb-1">
        <MessageCircle className="h-5 w-5 text-emerald-600" />
        <h3 className="font-serif text-lg font-bold text-[#2b1410]">Richieste rapide WhatsApp (camere)</h3>
        <div className="ml-auto flex gap-2">
          <button type="button" onClick={reset} className="inline-flex items-center gap-1 rounded-full border border-[#e8dcc8] px-3 py-1.5 text-xs text-[#7c5a4e] hover:bg-[#faf3ee]" data-testid="qr-reset-btn"><RotateCcw className="h-3.5 w-3.5" /> Ripristina default</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex items-center gap-1 rounded-full bg-[#4a1c17] hover:bg-[#2b1410] text-[#f7ebdf] px-4 py-1.5 text-xs font-medium disabled:opacity-60" data-testid="qr-save-btn">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva
          </button>
        </div>
      </div>
      <p className="text-xs text-[#7c5a4e] mb-4">Testi mostrati nella pagina camera dopo lo sblocco PIN. Usa <code className="bg-[#faf3ee] px-1 rounded">{"{nome}"}</code> e <code className="bg-[#faf3ee] px-1 rounded">{"{camera}"}</code> come segnaposto. Le etichette dei pulsanti vengono tradotte automaticamente (AI) al salvataggio; i messaggi WhatsApp restano sempre in italiano.</p>

      <div className="grid gap-3 sm:grid-cols-2 mb-4">
        <label className="text-xs text-[#7c5a4e] sm:col-span-2">Intro del messaggio
          <input value={cfg.intro} onChange={(e) => set({ intro: e.target.value })} className={`${inputCls} mt-1`} data-testid="qr-intro-input" />
        </label>
        <label className="text-xs text-[#7c5a4e]">Titolo pulsante generico
          <input value={cfg.ask_title} onChange={(e) => set({ ask_title: e.target.value })} className={`${inputCls} mt-1`} data-testid="qr-title-input" />
          <I18nFields value={cfg.ask_title_i18n} onChange={(v) => set({ ask_title_i18n: v })} testId="qr-title-i18n" />
        </label>
        <label className="text-xs text-[#7c5a4e]">Sottotitolo
          <input value={cfg.ask_subtitle} onChange={(e) => set({ ask_subtitle: e.target.value })} className={`${inputCls} mt-1`} data-testid="qr-subtitle-input" />
          <I18nFields value={cfg.ask_subtitle_i18n} onChange={(v) => set({ ask_subtitle_i18n: v })} testId="qr-subtitle-i18n" />
        </label>
        <label className="text-xs text-[#7c5a4e] sm:col-span-2">Testo dopo l'intro per il pulsante generico
          <input value={cfg.ask_text} onChange={(e) => set({ ask_text: e.target.value })} className={`${inputCls} mt-1`} data-testid="qr-asktext-input" />
        </label>
      </div>

      <div className="flex items-center justify-between mb-2">
        <h4 className="text-sm font-semibold text-[#2b1410]">Scorciatoie ({cfg.items.length})</h4>
        <button type="button" onClick={addItem} disabled={cfg.items.length >= 12} className="inline-flex items-center gap-1 text-xs text-[#4a1c17] hover:underline disabled:opacity-40" data-testid="qr-add-btn"><Plus className="h-3.5 w-3.5" /> Aggiungi</button>
      </div>
      <div className="space-y-2">
        {cfg.items.length === 0 && <div className="text-xs text-[#a08477] text-center py-3">Nessuna scorciatoia: verrà mostrato solo il pulsante generico.</div>}
        {cfg.items.map((it, i) => (
          <QuickItemRow key={it.id || `new-${i}`} item={it} idx={i} total={cfg.items.length}
            onChange={(p) => setItem(i, p)} onRemove={() => removeItem(i)} onMove={(d) => moveItem(i, d)} />
        ))}
      </div>
    </section>
  );
}
